// ---------------- GoogleAddressField.tsx (simplified) ----------------
import React, { useEffect, useRef } from 'react';
import { FilledInput } from '@mui/material';
import { useConfig } from '../../../config/useConfig';

interface Props {
  value: string;
  onChange: (addressObj: Record<string, string | number>) => void;
}

// Load Google Maps script once. Exported for reuse by the conversational-form
// address control (same idempotent script inject, same env key).
export function loadMaps(apiKey: string, cb: () => void) {
  if (typeof window === 'undefined') return;
  if ((window as any).google?.maps) { cb(); return; }
  const id = 'gmaps-js';
  if (document.getElementById(id)) { document.getElementById(id)!.addEventListener('load', cb); return; }
  const s = document.createElement('script');
  s.id = id;
  s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly`;
  s.async = true;
  s.onload = cb;
  document.body.appendChild(s);
}

// Parse a Places result into the `Address:*` bag. Exported for reuse by the
// conversational-form address control.
export function parse(place: any): Record<string, string | number> {
  const out: Record<string, any> = {};
  if (!place.address_components) return {};
  for (const c of place.address_components) {
    if (c.types.includes('street_number')) out.street_number = c.long_name;
    if (c.types.includes('route')) out.route = c.long_name;
    if (c.types.includes('locality')) out.city = c.long_name;
    if (c.types.includes('administrative_area_level_1')) out.state = c.short_name;
    if (c.types.includes('postal_code')) out.zip = c.long_name;
    if (c.types.includes('country')) out.country = c.long_name;
  }
  return {
    'Address:Street': [out.street_number, out.route].filter(Boolean).join(' '),
    'Address:City': out.city || '',
    'Address:State': out.state || '',
    'Address:PostalCode': out.zip || '',
    'Address:Country': out.country || '',
    'Address:Full': place.formatted_address || '',
    'Address:Lat': place.geometry?.location?.lat() || 0,
    'Address:Lng': place.geometry?.location?.lng() || 0,
  };
}

const GoogleAddressField: React.FC<Props> = ({ value, onChange }) => {
  const config = useConfig();
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) return;
    loadMaps(key, () => {
      if (!ref.current) return;
      const ac = new (window as any).google.maps.places.Autocomplete(ref.current, { types: ['geocode'] });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        onChange(parse(place));
      });
    });
  }, [onChange]);

  return (
    <FilledInput
      fullWidth
      inputRef={ref}
      defaultValue={value}
      placeholder="Start typing your address…"
      sx={{
        backgroundColor: config.MuiFilledInputBackground || 'white',
        borderRadius: `${config.BorderRadiusNumber || 4}px`,
      }}
    />
  );
};

export default GoogleAddressField; 